/**
 * GuaranteeRow — borrower Guarantees list row.
 *
 * 6-column grid: Borrower | Loan # (mono short id) | Amount | Type chip
 * (amber for limited, red otherwise) | Expiry (kept color-coding from
 * `getDaysUntilExpiration`) | Status chip (mint dot for active).
 *
 * Width template: `1.5fr 1fr 1fr 1fr 1fr 100px`.
 * 44px min-height row; whole row navigates to the case when clicked.
 */

'use client';

import { Box, baseColors, formatCurrencyWhole, GlassChip, Typography, useI18n } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';

export interface GuaranteeRowData {
  id: string;
  caseId: string;
  caseNumber?: string | null;
  guaranteeType: string;
  guaranteeAmount?: number | null;
  status: string;
  expirationDate?: string | null;
  borrowerName?: string | null;
}

export interface GuaranteeRowProps {
  guarantee: GuaranteeRowData;
  onClick?: (caseId: string) => void;
}

/** Shorten a UUID-style case number to the first two dash segments (per spec). */
function shortenLoanNumber(caseNumber?: string | null): string {
  if (!caseNumber) return '—';
  const segments = caseNumber.split('-');
  if (segments.length <= 2) return caseNumber;
  return segments.slice(0, 2).join('-');
}

/** Mirror of GuarantorHubPage's `getDaysUntilExpiration`; kept local to avoid a circular import. */
function getDaysUntilExpiration(expirationDate: string | null | undefined): number | null {
  if (!expirationDate) return null;
  const diff = new Date(expirationDate).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const EXPIRING_SOON_DAYS = 90;

function getExpirationColor(days: number | null): 'error.main' | 'warning.main' | 'text.secondary' {
  if (days === null) return 'text.secondary';
  if (days <= 0) return 'error.main';
  if (days <= EXPIRING_SOON_DAYS) return 'warning.main';
  return 'text.secondary';
}

function formatExpirationDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function GuaranteeRow({ guarantee, onClick }: GuaranteeRowProps): ReactElement {
  const { t } = useI18n();
  const expiryDays = getDaysUntilExpiration(guarantee.expirationDate);
  const expiryColor = getExpirationColor(expiryDays);
  const typeIsLimited = guarantee.guaranteeType.toLowerCase().includes('limited');
  const typeColor: 'warning' | 'error' = typeIsLimited ? 'warning' : 'error';
  const statusIsActive = guarantee.status?.toLowerCase() === 'active';
  const amount = guarantee.guaranteeAmount;

  return (
    <Box
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : -1}
      onClick={() => onClick?.(guarantee.caseId)}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick(guarantee.caseId);
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 100px',
        gap: 1.5,
        alignItems: 'center',
        minHeight: 56,
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 120ms ease',
        '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
        '&:focus-visible': onClick ? { outline: `2px solid ${baseColors.teal[400]}`, outlineOffset: -2 } : undefined,
      }}
    >
      {/* Borrower */}
      <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: 'text.primary' }}>
        {guarantee.borrowerName || t('common.unknown')}
      </Typography>

      {/* Loan # (mono short id) */}
      <Typography variant="caption" noWrap sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
        {shortenLoanNumber(guarantee.caseNumber)}
      </Typography>

      {/* Amount */}
      <Typography
        variant="body2"
        noWrap
        sx={{ fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}
      >
        {amount && amount > 0 ? formatCurrencyWhole(amount) : '—'}
      </Typography>

      {/* Type chip */}
      <Box>
        <GlassChip label={guarantee.guaranteeType} size="small" color={typeColor} variant="outlined" />
      </Box>

      {/* Expiry */}
      <Typography variant="caption" sx={{ color: expiryColor }}>
        {formatExpirationDate(guarantee.expirationDate)}
      </Typography>

      {/* Status chip */}
      <Box>
        <GlassChip label={guarantee.status} size="small" color={statusIsActive ? 'success' : 'default'} />
      </Box>
    </Box>
  );
}
